import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

export type BlockWithUniqueDocuments = Block & {
    uniqueDocuments?: boolean;
    query?: Record<string, unknown>;
    count?: number;
};

function generateLoadedDocuments(blocks, queryClient: QueryClient) {
    return blocks.reduce((acc, { uniqueDocuments = false, query, count }, index) => {
        if (!uniqueDocuments) {
            return [...acc, false];
        }
        const newQuery = {
            ...query,
            count,
            ...(index > 0
                ? {
                      exclude_id: acc
                          .slice(0, index)
                          .filter((it) => it !== null && it !== false)
                          .reduce((acc, it) => [...acc, ...(it || [])], []),
                  }
                : {}),
        };
        const { data = null } =
            queryClient.getQueryState<{ data: DocumentSnippet[] }>(['documents', newQuery]) || {};
        const { data: items = null } = data || {};
        return [...acc, items !== null ? items.map(({ id }) => id) : null];
    }, []);
}

export default function useUniqueDocumentsBlocks(blocks: BlockWithUniqueDocuments[]) {
    const queryClient = useQueryClient();
    const [loadedDocuments, setLoadedDocuments] = useState(() =>
        generateLoadedDocuments(blocks, queryClient),
    );
    const onDocumentsLoaded = (blockIndex, documents) => {
        setLoadedDocuments((currentLoadedDocuments) => {
            const newLoadedDocuments = [...currentLoadedDocuments];
            newLoadedDocuments[blockIndex] = documents.map(({ id }) => id);
            return newLoadedDocuments;
        });
    };

    const blocksCount = blocks.length;

    const [onLoaded, setOnLoaded] = useState(() =>
        [...Array(blocksCount).keys()].map((index) => (items) => onDocumentsLoaded(index, items)),
    );
    if (blocksCount > onLoaded.length) {
        setOnLoaded(
            [...Array(blocksCount).keys()].map(
                (index) => onLoaded[index] ?? ((items) => onDocumentsLoaded(index, items)),
            ),
        );
    }
    if (blocksCount > loadedDocuments.length) {
        setLoadedDocuments(generateLoadedDocuments(blocks, queryClient));
    }

    const newBlocks = blocks.map((block, blockIndex) => {
        const { uniqueDocuments = false, query, ...other } = block;
        return uniqueDocuments
            ? {
                  ...other,
                  query:
                      blockIndex > 0
                          ? {
                                ...query,
                                exclude_id: loadedDocuments
                                    .slice(0, blockIndex)
                                    .filter((it) => it !== null && it !== false)
                                    .reduce((acc, it) => [...acc, ...(it || [])], []),
                            }
                          : query,
                  disabled: loadedDocuments
                      .slice(0, blockIndex)
                      .reduce((acc, val) => acc || val === null, false),
                  onLoaded: onLoaded?.[blockIndex] ?? null,
              }
            : block;
    });

    return newBlocks;
}
